import MapStyleSwitcher from "../../components/MapStyleSwitcher";

/**
 * Full-width Mapbox map section on the customer-facing quote page.
 * All Mapbox refs live in the parent (QuoteViewPage); this component is
 * purely presentational and receives the already-initialized map container ref.
 */
const QuoteMapSection = ({
  mapContainerRef,
  mapRef,
  quote,
  showWetlands,
  setShowWetlands,
  onStyleLoad,
  calculateAcres,
  calculateTotalAcres,
}) => {
  const polygons = Array.isArray(quote.polygon_data)
    ? quote.polygon_data
    : [quote.polygon_data];

  const totalAcres = calculateTotalAcres(polygons);

  const handleWetlandsToggle = () => {
    if (!mapRef.current?.getLayer("nwi-wetlands-layer")) return;
    const next = !showWetlands;
    mapRef.current.setLayoutProperty("nwi-wetlands-layer", "visibility", next ? "visible" : "none");
    setShowWetlands(next);
  };

  return (
    <div className="border-b border-zinc-200 relative">
      <div
        ref={mapContainerRef}
        className="h-[500px] w-full"
        data-testid="property-map-full"
      />

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-lg p-3 z-10">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Legend</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 border-2 border-[#4169E1] bg-[#4169E1]/20 rounded-sm" />
            <span className="text-xs text-zinc-700">Property Lines</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 border-2 border-[#dc2626] bg-[#dc2626]/30 rounded-sm" />
            <span className="text-xs text-zinc-700">Clearing Area</span>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-zinc-200 flex gap-2">
          <button
            onClick={handleWetlandsToggle}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-colors ${
              showWetlands ? "bg-cyan-600 text-white" : "bg-zinc-100 text-zinc-600"
            }`}
          >
            Wetlands
          </button>
        </div>
      </div>

      <div className="absolute bottom-4 right-4 z-10">
        <MapStyleSwitcher mapRef={mapRef} onStyleLoad={onStyleLoad} />
      </div>

      {/* Multi-area breakdown panel */}
      {polygons.length > 1 && (
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur rounded-lg shadow-lg p-4 z-10 min-w-[180px]">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">
            Area Breakdown
          </p>
          <div className="space-y-2">
            {polygons.map((poly, idx) => (
              <div key={`area-map-${idx}`} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#dc2626] flex items-center justify-center">
                    <span className="text-xs font-bold text-white">{idx + 1}</span>
                  </div>
                  <span className="text-sm text-zinc-700">{poly._name || `Area ${idx + 1}`}</span>
                </div>
                <span className="text-sm font-semibold text-zinc-900">
                  {calculateAcres(poly).toFixed(2)} ac
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-200 flex justify-between">
            <span className="text-sm font-semibold text-[#00341d]">Total</span>
            <span className="text-sm font-bold text-[#00341d]">{totalAcres.toFixed(2)} ac</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteMapSection;
